/**
 * Mock drift data for development.
 * This file will be replaced by actual API calls once the backend is ready.
 */
import type { DriftReport } from '../../types/analysis.types';

/** Current timestamp so demo data always looks recent. */
const now = new Date().toISOString();

export const mockDriftReport: DriftReport = {
  analysisDate: now,
  repositoryName: 'CrowdStrike/caracara',
  projectType: 'library',
  primaryLanguage: 'Python',
  score: 21,
  grade: 'D',
  metrics: {
    totalDependencies: 77,
    outdatedDependencies: 61,
    driftRatio: 0.792,
    oldestPinnedYears: 7.0,
    shareOlderThan2Years: 0.23,
    shareOlderThan4Years: 0.05,
  },
  ageDistribution: [
    { bucket: '7+', count: 0, examples: [] },
    { bucket: '4-6', count: 4, examples: ['dill 0.2.9', 'toml 0.10.2'] },
    { bucket: '2-4', count: 14, examples: ['mccabe 0.7.0', 'tabulate 0.9.0'] },
    { bucket: '<2', count: 59, examples: ['pyflakes 3.2.0', 'wcwidth 0.2.13'] },
  ],
  staleDependencies: [
    { name: 'dill', pinned: '0.2.9', latest: '0.4.0', ageYears: 7.0, type: 'direct' },
    { name: 'snowballstemmer', pinned: '2.2.0', latest: '3.0.1', ageYears: 4.1, type: 'direct' },
    { name: 'markdown-it-py', pinned: '3.0.0', latest: '4.0.0', ageYears: 2.6, type: 'direct' },
    { name: 'brotli', pinned: '1.1.0', latest: '1.2.0', ageYears: 2.3, type: 'direct' },
    { name: 'isort', pinned: '5.13.2', latest: '7.0.0', ageYears: 2.1, type: 'dev' },
    { name: 'requests', pinned: '2.28.0', latest: '2.31.0', ageYears: 1.8, type: 'direct' },
    { name: 'urllib3', pinned: '1.26.9', latest: '2.2.0', ageYears: 1.5, type: 'transitive' },
    {
      name: 'certifi',
      pinned: '2023.7.22',
      latest: '2024.8.30',
      ageYears: 1.2,
      type: 'transitive',
    },
    { name: 'packaging', pinned: '23.1', latest: '24.0', ageYears: 0.9, type: 'transitive' },
    { name: 'typing-extensions', pinned: '4.7.1', latest: '4.12.0', ageYears: 0.8, type: 'direct' },
  ],
  security: {
    depsOlderThan12Mo: 48,
    depsOlderThan24Mo: 18,
    riskLevel: 'high',
    scanTools: ['pip-audit', 'safety', 'osv-scanner'],
  },
  interpretation: {
    summary:
      'Python security utility with sizeable dependency set including security-relevant networking libraries.',
    bottomLine:
      '61 of 77 dependencies are behind latest versions. Incremental, automated maintenance would reduce long-term risk.',
    riskImplications:
      'High drift increases upgrade complexity and expands security exposure windows.',
  },
};

// Alternative mock data for testing different states
export const mockHealthyDriftReport: DriftReport = {
  analysisDate: now,
  repositoryName: 'example/healthy-project',
  projectType: 'application',
  primaryLanguage: 'TypeScript',
  score: 92,
  grade: 'A',
  metrics: {
    totalDependencies: 45,
    outdatedDependencies: 4,
    driftRatio: 0.089,
    oldestPinnedYears: 1.2,
    shareOlderThan2Years: 0.02,
    shareOlderThan4Years: 0.0,
  },
  ageDistribution: [
    { bucket: '7+', count: 0, examples: [] },
    { bucket: '4-6', count: 0, examples: [] },
    { bucket: '2-4', count: 1, examples: ['lodash 4.17.21'] },
    { bucket: '<2', count: 44, examples: ['react 18.2.0', 'typescript 5.4.0'] },
  ],
  staleDependencies: [
    { name: 'lodash', pinned: '4.17.21', latest: '4.18.0', ageYears: 2.1, type: 'direct' },
    { name: 'moment', pinned: '2.29.4', latest: '2.30.0', ageYears: 0.8, type: 'dev' },
    { name: 'axios', pinned: '1.5.0', latest: '1.6.0', ageYears: 0.4, type: 'direct' },
    { name: 'uuid', pinned: '9.0.0', latest: '9.0.1', ageYears: 0.3, type: 'transitive' },
  ],
  security: {
    depsOlderThan12Mo: 5,
    depsOlderThan24Mo: 1,
    riskLevel: 'low',
    scanTools: ['npm audit', 'snyk', 'dependabot'],
  },
  interpretation: {
    summary: 'Well-maintained TypeScript application with active dependency management.',
    bottomLine:
      'Only 4 of 45 dependencies are slightly behind. Current maintenance practices are effective.',
    riskImplications:
      'Low drift keeps upgrade complexity manageable and minimizes security exposure.',
  },
};

export const mockModerateDriftReport: DriftReport = {
  analysisDate: now,
  repositoryName: 'example/moderate-project',
  projectType: 'service',
  primaryLanguage: 'Go',
  score: 72,
  grade: 'C',
  metrics: {
    totalDependencies: 32,
    outdatedDependencies: 12,
    driftRatio: 0.375,
    oldestPinnedYears: 3.5,
    shareOlderThan2Years: 0.15,
    shareOlderThan4Years: 0.0,
  },
  ageDistribution: [
    { bucket: '7+', count: 0, examples: [] },
    { bucket: '4-6', count: 0, examples: [] },
    { bucket: '2-4', count: 5, examples: ['gin-gonic/gin v1.9.0', 'spf13/cobra v1.7.0'] },
    { bucket: '<2', count: 27, examples: ['go.uber.org/zap v1.27.0', 'stretchr/testify v1.9.0'] },
  ],
  staleDependencies: [
    { name: 'gin-gonic/gin', pinned: 'v1.9.0', latest: 'v1.10.0', ageYears: 3.5, type: 'direct' },
    { name: 'spf13/cobra', pinned: 'v1.7.0', latest: 'v1.8.1', ageYears: 2.8, type: 'direct' },
    { name: 'gorm.io/gorm', pinned: 'v1.24.0', latest: 'v1.25.7', ageYears: 2.1, type: 'direct' },
    { name: 'redis/go-redis', pinned: 'v9.2.0', latest: 'v9.5.0', ageYears: 1.4, type: 'direct' },
    {
      name: 'prometheus/client_golang',
      pinned: 'v1.17.0',
      latest: 'v1.19.0',
      ageYears: 0.9,
      type: 'direct',
    },
  ],
  security: {
    depsOlderThan12Mo: 15,
    depsOlderThan24Mo: 5,
    riskLevel: 'medium',
    scanTools: ['govulncheck', 'nancy', 'trivy'],
  },
  interpretation: {
    summary:
      'Go microservice with moderate dependency drift. Core framework dependencies need attention.',
    bottomLine:
      '12 of 32 dependencies are behind latest versions. Schedule updates for web framework and CLI dependencies.',
    riskImplications:
      'Moderate drift may complicate future updates. Consider scheduling quarterly dependency reviews.',
  },
};

/**
 * Demo drift report for Crescendo.
 * Matches the vibeMapperAnalysis demo data.
 */
export const vibeMapperDriftReport: DriftReport = {
  analysisDate: now,
  repositoryName: 'crescendo-labs/crescendo',
  projectType: 'application',
  primaryLanguage: 'TypeScript',
  score: 34,
  grade: 'D',
  metrics: {
    totalDependencies: 370,
    outdatedDependencies: 240,
    driftRatio: 0.65,
    oldestPinnedYears: 5.8,
    shareOlderThan2Years: 0.05,
    shareOlderThan4Years: 0.01,
  },
  ageDistribution: [
    { bucket: '7+', count: 0, examples: [] },
    {
      bucket: '4-6',
      count: 4,
      examples: ['fqdn', 'isoduration', 'rfc3339-validator', 'rfc3986-validator'],
    },
    {
      bucket: '2-4',
      count: 14,
      examples: ['backoff', 'colorama', 'distro', 'filetype', 'jsonpatch'],
    },
    {
      bucket: '<2',
      count: 176,
      examples: ['streamlit', 'pandas', 'openpyxl', 'numpy', 'langchain'],
    },
  ],
  staleDependencies: [
    { name: 'docker', pinned: '7.0', latest: '7.1.0', ageYears: 1.72, type: 'direct' },
    { name: 'httpx', pinned: '0.27', latest: '0.28.1', ageYears: 1.18, type: 'direct' },
    { name: 'loguru', pinned: '0.7.2', latest: '0.7.3', ageYears: 1.18, type: 'direct' },
    { name: 'tiktoken', pinned: '0.7.0', latest: '0.12.0', ageYears: 0.34, type: 'direct' },
    { name: 'pydantic-settings', pinned: '2.0', latest: '2.12.0', ageYears: 0.25, type: 'direct' },
    { name: 'pytest-asyncio', pinned: '0.21', latest: '1.3.0', ageYears: 0.25, type: 'direct' },
    { name: 'pytest-mock', pinned: '3.11', latest: '3.15.1', ageYears: 0.4, type: 'direct' },
    { name: 'tree-sitter', pinned: '0.22', latest: '0.25.2', ageYears: 0.37, type: 'direct' },
    { name: 'pyyaml', pinned: '6.0', latest: '6.0.3', ageYears: 0.37, type: 'direct' },
    { name: 'websocket-client', pinned: '1.7', latest: '1.9.0', ageYears: 0.34, type: 'direct' },
  ],
  security: {
    depsOlderThan12Mo: 18,
    depsOlderThan24Mo: 14,
    riskLevel: 'medium',
    scanTools: [],
  },
  interpretation: {
    summary:
      'Full-stack TypeScript/Python monorepo with significant dependency drift across 370 packages spanning npm and PyPI ecosystems.',
    bottomLine:
      '240 of 370 dependencies are behind latest versions. No dependency automation (Dependabot/Renovate) is configured.',
    riskImplications:
      'High drift ratio increases upgrade complexity and expands security exposure windows. Automated dependency management is recommended.',
  },
};
